import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_WORK_ORDER_PRIORITIES, SCOPE_CHANGE_IMPACTS } from '@lims/shared-types';
import {
  catalogueApi,
  clientsApi,
  organisationApi,
  projectsApi,
  quotationsApi,
  settingsApi,
  workOrdersApi,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  CheckboxField,
  DateField,
  ErrorState,
  FormSection,
  LoadingState,
  Panel,
  RadioField,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
  FormPage,
  useLoadOnce,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { optionalText } from '../clients/bilingual.js';
import {
  ServiceLineEditor,
  ServiceRefusal,
  draftFromService,
  emptyServiceLine,
  servicePayload,
} from './ServiceLineEditor.js';
import type { ServiceLineDraft } from './ServiceLineEditor.js';

/**
 * Raise a request, or edit the details of one already raised.
 *
 * ===========================================================================
 * A REQUEST IS RAISED BEFORE ANY SAMPLE EXISTS, AND THE PAGE SAYS SO.
 * ===========================================================================
 *
 * That is the ordinary sequence at a reception desk: the client asks for work,
 * the job is booked, the material follows. The form therefore asks for nothing
 * about a sample, and the notice above it states plainly that saving this
 * records a REQUEST — registering one is not evidence that anything arrived.
 *
 * TWO SOURCES, AND ONLY ONE OF THEM PRICES ANYTHING HERE.
 *
 *  - FROM AN ACCEPTED QUOTATION: the services and their prices are carried
 *    across by the server as SNAPSHOTS of what the client agreed to. This page
 *    shows them and lets nobody edit them, because a reprice between
 *    acceptance and the job must never change what the client is charged.
 *  - CHOSEN DIRECTLY: the server resolves each price — the client's agreement
 *    first, then the catalogue — and records where it came from. The unit
 *    price is left EMPTY for that reason. Typing one is an override needing
 *    `pricing.override` and a stated reason.
 *
 * A DUE DATE IS OPTIONAL AND IS NEVER FILLED IN FOR ANYBODY. Left blank it
 * means the laboratory promised no date; defaulting it to today, or to the
 * request date, would put a commitment on the record that nobody made — and
 * the list would then report the job late against it.
 *
 * EDITING CHANGES THE SERVICES TOO, WITHIN THE LIFECYCLE (D195). Until then the
 * edit form could not touch them at all, and the scope dialog re-created every
 * line — re-pricing it from today's catalogue and renumbering it. Now a saved
 * service is edited IN PLACE (its `id` travels with it): quantity, department,
 * price where permitted, discount and notes. A service whose work has started
 * is shown and locked; removing a service that a test is already assigned
 * against, or that is billed, is refused by the server with the reason. Once
 * work has started on the job, a change to its services asks for the three
 * acknowledgements, exactly as the scope dialog does. The services are saved
 * FIRST and the details second, and the rows take the ids the server gave
 * them, so a retry after a failed second step can never add a line twice.
 */
export function WorkOrderFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const isNew = id === undefined;
  const canEdit = can(
    isNew ? PERMISSIONS.WORK_ORDERS_CREATE : PERMISSIONS.WORK_ORDERS_UPDATE,
  );
  const canOverride = can(PERMISSIONS.PRICING_OVERRIDE);

  const [source, setSource] = useState<string>(
    searchParams.get('quotationId') ? 'quotation' : 'direct',
  );
  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [quotationId, setQuotationId] = useState<string | null>(
    searchParams.get('quotationId'),
  );
  const [projectId, setProjectId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [requestingPartyName, setRequestingPartyName] = useState('');
  const [requestingPartyEmail, setRequestingPartyEmail] = useState('');
  const [requestingPartyPhone, setRequestingPartyPhone] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [clientReference, setClientReference] = useState('');
  const [requestDate, setRequestDate] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [services, setServices] = useState<ServiceLineDraft[]>([
    emptyServiceLine(),
  ]);
  /** What the saved services look like as a request, to tell whether any changed. */
  const [savedServices, setSavedServices] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState({
    price: false,
    deadline: false,
    workInProgress: false,
  });
  const [acknowledgementNote, setAcknowledgementNote] = useState('');

  const workOrder = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => workOrdersApi.get(id as string),
    enabled: !isNew,
  });

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const projects = useQuery({
    queryKey: ['projects', 'selectable', clientId],
    queryFn: () =>
      projectsApi.list({ pageSize: 100, ...(clientId ? { clientId } : {}) }),
    enabled: can(PERMISSIONS.PROJECTS_VIEW),
  });

  const catalogue = useQuery({
    queryKey: ['catalogue', 'selectable'],
    queryFn: () => catalogueApi.list({ pageSize: 100, includeInactive: false }),
  });

  /**
   * The laboratory's own branches and departments.
   *
   * Both lists are READ by anyone signed in, because this form needs them and
   * a person raising a job is not thereby entitled to manage the structure.
   * Neither list is seeded with anything: what a laboratory calls its branches
   * belongs to that laboratory (CLAUDE.md section 6.1).
   */
  const branches = useQuery({
    queryKey: ['branches', 'active'],
    queryFn: () => organisationApi.listBranches(false),
  });

  const departments = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: () => organisationApi.listDepartments(false),
  });

  /** Only accepted quotations can hand anything on; the API refuses the rest. */
  const quotations = useQuery({
    queryKey: ['quotations', 'accepted', clientId],
    queryFn: () =>
      quotationsApi.list({
        pageSize: 100,
        status: 'accepted',
        ...(clientId ? { clientId } : {}),
      }),
    enabled: isNew && can(PERMISSIONS.QUOTATIONS_VIEW),
  });

  /** What the chosen quotation carries — and whether a job was already raised. */
  const handover = useQuery({
    queryKey: ['quotation-handover', quotationId],
    queryFn: () => quotationsApi.handover(quotationId as string),
    enabled: isNew && source === 'quotation' && Boolean(quotationId),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
  });

  useLoadOnce(workOrder.data, (record) => {
    setClientId(record.clientId);
    setQuotationId(record.quotationId);
    setProjectId(record.projectId);
    setBranchId(record.branch?.id ?? null);
    setRequestingPartyName(record.requestingParty.name ?? '');
    setRequestingPartyEmail(record.requestingParty.email ?? '');
    setRequestingPartyPhone(record.requestingParty.phone ?? '');
    setPurchaseOrderNumber(record.purchaseOrderNumber ?? '');
    setClientReference(record.clientReference ?? '');
    setRequestDate(record.requestDate);
    setDueDate(record.dueDate);
    setPriority(record.priority);
    setSubject(record.subject ?? '');
    setNotes(record.notes ?? '');
    // Cancelled services are history, not scope: they stay on the file page.
    const drafts = record.services
      .filter((service) => service.state !== 'cancelled')
      .map(draftFromService);
    setServices(drafts);
    setSavedServices(JSON.stringify(servicePayload(drafts)));
  });

  const servicesChanged =
    !isNew && savedServices !== null && JSON.stringify(servicePayload(services)) !== savedServices;
  const needsAcknowledgement =
    servicesChanged && (workOrder.data?.scopeChangeNeedsAcknowledgement ?? false);
  const servicesEditable =
    canEdit && (isNew || (workOrder.data?.acceptsScopeChange ?? false));

  const save = useMutation({
    mutationFn: async () => {
      const shared = {
        ...(projectId ? { projectId } : {}),
        ...(branchId ? { branchId } : {}),
        requestingPartyName: optionalText(requestingPartyName),
        requestingPartyEmail: optionalText(requestingPartyEmail),
        requestingPartyPhone: optionalText(requestingPartyPhone),
        purchaseOrderNumber: optionalText(purchaseOrderNumber),
        clientReference: optionalText(clientReference),
        ...(requestDate ? { requestDate } : {}),
        // Sent as an explicit `null` when cleared, so the laboratory can take
        // a promised date back off the record rather than being stuck with it.
        dueDate,
        ...(priority ? { priority } : {}),
        subject: optionalText(subject),
        notes: optionalText(notes),
      };

      if (!isNew) {
        // Services first: the step the server is likelier to refuse, so a
        // refusal leaves nothing half-saved. The rows then take the ids the
        // server gave them, so retrying after a failed second step can never
        // add a line twice.
        if (servicesChanged) {
          const saved = await workOrdersApi.setServices(id as string, {
            services: servicePayload(services),
            ...(acknowledged.price && acknowledged.deadline && acknowledged.workInProgress
              ? {
                  impactAcknowledgement: {
                    price: true as const,
                    deadline: true as const,
                    workInProgress: true as const,
                    ...(acknowledgementNote.trim() ? { note: acknowledgementNote.trim() } : {}),
                  },
                }
              : {}),
          });
          const drafts = saved.services
            .filter((service) => service.state !== 'cancelled')
            .map(draftFromService);
          setServices(drafts);
          setSavedServices(JSON.stringify(servicePayload(drafts)));
        }
        return workOrdersApi.update(id as string, shared);
      }

      return workOrdersApi.create({
        ...shared,
        clientId,
        // Exactly one source. Sending both is refused by the API, because two
        // lists for one job is how an agreed price and a charged price part
        // company.
        ...(source === 'quotation'
          ? { quotationId }
          : { services: servicePayload(services) }),
      });
    },
    onSuccess: async (saved) => {
      // The work order's page shows what was just saved, not a cached copy.
      queryClient.setQueryData(['work-order', saved.id], saved);
      await queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      await queryClient.invalidateQueries({
        queryKey: ['work-order', saved.id],
      });
      await queryClient.invalidateQueries({ queryKey: ['quotation-handover'] });
      show({ tone: 'success', message: t('workOrders.saved') });
      navigate(`/work-orders/${saved.id}`, { replace: true });
    },
  });


  if (!isNew && workOrder.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (!isNew && workOrder.error) {
    return (
      <ErrorState
        error={workOrder.error}
        onRetry={() => void workOrder.refetch()}
      />
    );
  }

  const currency = workOrder.data?.currency ?? settings.data?.currency;
  const alreadyRaised =
    handover.data?.workOrder.state === 'raised'
      ? handover.data.workOrder
      : null;

  return (
    <FormPage
      title={isNew ? t('workOrders.createTitle') : t('workOrders.editTitle')}
      breadcrumbs={[
        { label: t('nav.workOrders'), to: '/work-orders' },
        {
          label: isNew
            ? t('workOrders.createTitle')
            : (workOrder.data?.workOrderNumber ?? t('workOrders.editTitle')),
        },
      ]}
      onSubmit={() => save.mutate()}
      onCancel={() => navigate(isNew ? '/work-orders' : `/work-orders/${id}`)}
      isSubmitting={save.isPending}
      canSubmit={canEdit}
      error={save.error}
      fieldLabels={{ clientId: t('workOrders.client'), dueDate: t('workOrders.dueDate'), requestDate: t('workOrders.requestDate') }}
      notices={
        <>
          {/*
            Stated above the form, not buried in help text. This is the acceptance
            criterion made visible to the person filling it in.
          */}
          {isNew ? (
            <div className="mb-stack">
              <Alert
                tone="info"
                title={t('workOrders.awaitingSampleNotice')}
                description={t('workOrders.awaitingSampleHint')}
              />
            </div>
          ) : (
            <div className="mb-stack">
              <Alert
                tone="info"
                title={t('workOrders.editScopeNotice')}
                description={t('workOrders.editScopeHint')}
              />
            </div>
          )}
          {alreadyRaised ? (
            <div className="mb-stack">
              <Alert
                tone="warning"
                title={t('workOrders.quotationAlreadyRaised', {
                  number: alreadyRaised.workOrderNumber,
                })}
                action={
                  <ActionButton
                    priority="secondary"
                    size="sm"
                    onPress={() => navigate(`/work-orders/${alreadyRaised.id}`)}
                  >
                    {t('common.open')}
                  </ActionButton>
                }
              />
            </div>
          ) : null}
        </>
      }
    >
        <FormSection title={t('workOrders.requestTitle')} columns={1}>
          {isNew ? (
            <RadioField
              label={t('workOrders.source')}
              value={source}
              onChange={(value) => {
                setSource(value);
                if (value === 'direct') setQuotationId(null);
              }}
              options={[
                {
                  value: 'direct',
                  label: t('workOrders.sourceDirect'),
                  description: t('workOrders.sourceDirectHint'),
                },
                {
                  value: 'quotation',
                  label: t('workOrders.sourceQuotation'),
                  description: t('workOrders.sourceQuotationHint'),
                },
              ]}
            />
          ) : null}

          <SelectField
            label={t('workOrders.client')}
            placeholder={t('workOrders.selectClient')}
            value={clientId}
            onChange={(value) => {
              setClientId(value);
              // The quotation and the project both belong to a client, so a
              // change of client invalidates both rather than leaving a
              // mismatched pair the server would have to refuse.
              setQuotationId(null);
              setProjectId(null);
            }}
            // A work order belongs to a client who is on file: unlike a
            // quotation, work is not carried out for a prospect.
            isRequired
            isDisabled={!isNew}
            isLoading={clients.isLoading}
            options={(clients.data?.data ?? []).map((client) => ({
              value: client.id,
              label: client.name || client.clientNumber,
              ...(client.clientNumber ? { description: client.clientNumber } : {}),
            }))}
          />

          {isNew && source === 'quotation' ? (
            <SelectField
              label={t('workOrders.quotation')}
              placeholder={t('workOrders.selectQuotation')}
              description={t('workOrders.selectQuotationHint')}
              value={quotationId}
              onChange={setQuotationId}
              isRequired
              isLoading={quotations.isLoading}
              options={(quotations.data?.data ?? []).map((quotation) => ({
                value: quotation.id,
                label: quotation.quotationNumber,
                ...(formatMoney(quotation.totalMinor, {
                  locale,
                  currency: quotation.currency,
                })
                  ? {
                      description: formatMoney(quotation.totalMinor, {
                        locale,
                        currency: quotation.currency,
                      }) as string,
                    }
                  : {}),
              }))}
            />
          ) : null}
        </FormSection>

        <FormSection title={t('workOrders.jobTitle')}>
          <SelectField
            label={t('workOrders.project')}
            placeholder={t('common.none')}
            value={projectId}
            onChange={setProjectId}
            isLoading={projects.isLoading}
            options={(projects.data?.data ?? []).map((project) => ({
              value: project.id,
              label: project.name || project.reference || '',
            }))}
          />
          {/*
            The laboratory's own branches. Empty until it creates one — the
            product ships no branch of its own, not even a "Main Branch", which
            would be this system asserting an identity belonging to a customer
            (decision D53).
          */}
          <SelectField
            label={t('workOrders.branch')}
            placeholder={t('common.none')}
            description={
              (branches.data?.data ?? []).length === 0
                ? t('workOrders.noBranchesYet')
                : undefined
            }
            value={branchId}
            onChange={setBranchId}
            isLoading={branches.isLoading}
            options={(branches.data?.data ?? []).map((branch) => ({
              value: branch.id,
              label: branch.name || branch.code || '',
            }))}
          />
          <DateField
            label={t('workOrders.requestDate')}
            description={t('workOrders.requestDateHint')}
            value={requestDate}
            onChange={setRequestDate}
          />
          {/*
            OPTIONAL, AND NEVER PRE-FILLED. Blank means no date was promised.
          */}
          <DateField
            label={t('workOrders.dueDate')}
            description={t('workOrders.dueDateHint')}
            value={dueDate}
            onChange={setDueDate}
          />
          <SelectField
            label={t('workOrders.priorityLabel')}
            description={t('workOrders.priorityHint')}
            placeholder={t('common.notSet')}
            value={priority}
            onChange={setPriority}
            options={ALL_WORK_ORDER_PRIORITIES.map((value) => ({
              value,
              label: t(`workOrderPriority.${value}`),
            }))}
          />
        </FormSection>

        <FormSection title={t('workOrders.subject')} columns={1}>
          <TextField
            label={t('workOrders.subject')}
            value={subject}
            onChange={setSubject}
            textDirection="auto"
          />
        </FormSection>

        <FormSection
          title={t('workOrders.requestingParty')}
          description={t('workOrders.requestingPartyHint')}
        >
          <TextField
            label={t('workOrders.requestingPartyName')}
            value={requestingPartyName}
            onChange={setRequestingPartyName}
          />
          <TextField
            label={t('workOrders.requestingPartyEmail')}
            type="email"
            value={requestingPartyEmail}
            onChange={setRequestingPartyEmail}
          />
          <TextField
            label={t('workOrders.requestingPartyPhone')}
            type="tel"
            value={requestingPartyPhone}
            onChange={setRequestingPartyPhone}
          />
          <TextField
            label={t('workOrders.purchaseOrderNumber')}
            description={t('workOrders.purchaseOrderHint')}
            value={purchaseOrderNumber}
            onChange={setPurchaseOrderNumber}
          />
          <TextField
            label={t('workOrders.clientReference')}
            value={clientReference}
            onChange={setClientReference}
          />
        </FormSection>

        {isNew && source === 'quotation' ? (
          <Panel
            title={t('workOrders.servicesTitle')}
            description={t('workOrders.snapshotHint')}
          >
            {handover.isLoading ? (
              <LoadingState label={t('common.loading')} />
            ) : handover.data ? (
              // One row per agreed service, divided by hairlines (D278).
              <ul className="-my-2 flex flex-col divide-y divide-default-200">
                {handover.data.services.map((line) => (
                  <li key={line.id} className="min-w-0 py-2">
                    <p className="text-body text-default-900">
                      {(locale === 'ar'
                        ? line.description.ar
                        : line.description.en) ?? t('common.notRecorded')}
                    </p>
                    <p className="measure text-caption text-default-700" dir="ltr">
                      {line.quantity} {'×'}{' '}
                      {formatMoney(line.unitPriceMinor, {
                        locale,
                        currency: handover.data?.currency ?? '',
                      })}
                    </p>
                    {/*
                      Where the agreed price came from, carried across with the
                      price. The job records what the client agreed to, not
                      what the catalogue says today.
                    */}
                    <p className="text-caption text-default-600">
                      {t(`priceSource.${line.priceSource}`)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body text-default-600">
                {t('workOrders.selectQuotationFirst')}
              </p>
            )}
          </Panel>
        ) : null}

        {isNew && source === 'direct' ? (
          <Panel
            title={t('workOrders.servicesTitle')}
            description={t('workOrders.servicesHint')}
            actions={
              canEdit ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() =>
                    setServices((current) => [...current, emptyServiceLine()])
                  }
                >
                  {t('workOrders.addService')}
                </ActionButton>
              ) : null
            }
          >
            <ServiceLineEditor
              lines={services}
              onChange={setServices}
              catalogue={catalogue.data?.data ?? []}
              departments={departments.data?.data ?? []}
              canOverride={canOverride}
              {...(currency ? { currency } : {})}
            />
          </Panel>
        ) : null}

        {!isNew ? (
          <Panel
            title={t('workOrders.servicesTitle')}
            description={
              servicesEditable ? t('workOrders.editServicesHint') : t('workOrders.servicesNotEditable')
            }
            actions={
              servicesEditable ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() =>
                    setServices((current) => [...current, emptyServiceLine()])
                  }
                >
                  {t('workOrders.addService')}
                </ActionButton>
              ) : null
            }
          >
            <ServiceLineEditor
              lines={services}
              onChange={setServices}
              catalogue={catalogue.data?.data ?? []}
              departments={departments.data?.data ?? []}
              canOverride={canOverride}
              isDisabled={!servicesEditable}
              {...(currency ? { currency } : {})}
            />
            {needsAcknowledgement ? (
              <fieldset className="mt-gutter min-w-0 border-0 p-0" data-testid="scope-acknowledgement">
                <legend className="mb-stack text-section font-medium text-default-800">
                  {t('workOrders.ackTitle')}
                </legend>
                <p className="mb-stack text-body text-default-600">
                  {t('workOrders.scopeStartedHint')}
                </p>
                <div className="flex flex-col gap-stack">
                  {SCOPE_CHANGE_IMPACTS.map((impact) => (
                    <CheckboxField
                      key={impact}
                      label={t(`workOrders.ack.${impact}`)}
                      description={t(`workOrders.ackHint.${impact}`)}
                      value={acknowledged[impact]}
                      onChange={(value) =>
                        setAcknowledged((current) => ({ ...current, [impact]: value }))
                      }
                    />
                  ))}
                </div>
                <div className="mt-stack">
                  <TextAreaField
                    label={t('workOrders.ackNote')}
                    value={acknowledgementNote}
                    onChange={setAcknowledgementNote}
                    rows={2}
                  />
                </div>
              </fieldset>
            ) : null}
          </Panel>
        ) : null}

        <FormSection title={t('workOrders.notes')} columns={1}>
          <TextAreaField
            label={t('workOrders.notes')}
            value={notes}
            onChange={setNotes}
            rows={3}
          />
        </FormSection>

        <ServiceRefusal error={save.error} />
        {save.error ? <ErrorState error={save.error} /> : null}
    </FormPage>
  );
}
